import { useEffect, useRef, useState } from "react";
import type { RescueDecision } from "@io-spoke/contracts/rescue";
import { getAccessToken, getActiveOrgId, getStoredUser } from "../lib/authStorage";
import { getRescueDecision, prepareRescueDecision, respondRescueDecision, RescueDecisionConflict } from "../lib/rescueDecisions";
import { useAsyncAction } from "./useAsyncAction";

type Operation = "load" | "prepare" | "approve" | "cancel";
function identity() {
  const token = getAccessToken();
  let principal = getStoredUser()?.email ?? "";
  if (token) {
    try {
      const encoded = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const claims = JSON.parse(atob(encoded));
      if (typeof claims.sub === "string") principal = claims.sub;
    } catch { principal = token; }
  }
  return `${getActiveOrgId() ?? ""}:${principal}:${Boolean(token)}`;
}

/** A mounted case owns its requests; every operation reconciles against durable server state. */
export function useRescueDecision(caseId: string, onCompleted?: (entityId: string) => void) {
  const action = useAsyncAction();
  const mounted = useRef(true);
  const scope = useRef({ identity: identity(), version: 0 });
  const [decision, setDecision] = useState<RescueDecision | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completed = useRef(onCompleted);
  completed.current = onCompleted;

  const run = (kind: Operation) => action.run(async () => {

    const actor = identity();
    const version = scope.current.version;
    const current = () => mounted.current && identity() === actor && scope.current.version === version;
    setOperation(kind); setError(null);
    const apply = (result: RescueDecision | null) => {
      if (!current()) return;
      if (result && result.rescueCaseId !== caseId) throw new Error("The recovery decision belongs to a different case.");
      setDecision(result); setLoaded(true);
      if (result?.status === "completed" && result.entityId) completed.current?.(result.entityId);
    };
    try {
      if (kind === "load") apply(await getRescueDecision(caseId));
      else if (kind === "prepare") apply(await prepareRescueDecision(caseId));
      else if (decision) apply(await respondRescueDecision(caseId, decision.id, { answer: kind }));
    } catch (cause) {
      if (!current()) return;
      if (cause instanceof RescueDecisionConflict) {
        try { apply(await getRescueDecision(caseId)); }
        catch { if (current()) setLoaded(false); }
      }
      if (current()) setError(cause instanceof Error ? cause.message : "The recovery decision could not be updated. Try again.");
    } finally {
      if (current()) setOperation(null);
    }
  });

  useEffect(() => {
    const contextChanged = () => {
      const next = identity();
      if (scope.current.identity === next) return;
      scope.current = { identity: next, version: scope.current.version + 1 };
      setDecision(null); setLoaded(false); setOperation(null);
      setError("Your account or workspace changed. Reload your saved plan.");
    };
    window.addEventListener("auth:changed", contextChanged);
    window.addEventListener("storage", contextChanged);
    return () => {
      window.removeEventListener("auth:changed", contextChanged);
      window.removeEventListener("storage", contextChanged);
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    void run("load");
    return () => { mounted.current = false; };
  }, [caseId]);
  return { decision, loaded, error, pending: action.pending, operation, run };
}
